import { BodyOf } from "../../core/api";
import {
  AgencyMemberLicenseId,
  VisitInstanceDailyNoteAttestationId,
  VisitInstanceDailyNoteId,
} from "../../shared/schema/schema";
import { createMutationGroup } from "../../core/api/mutations";

export default createMutationGroup(({ api, createMutation }) => ({
  /**
   * Publish one daily note to DoCare again.
   *
   * Every signed note is queued automatically; this is the recovery for one that
   * exhausted its retries, or that went out before something about it was corrected.
   *
   * Safe to press twice. DoCare's ingest is idempotent on the file name we send, which
   * is built from values that do not change, so a repeat updates the stored document
   * rather than adding a second one to the patient's profile.
   */
  requeueDocareUpload: () =>
    createMutation({
      mutationFn: (visitInstanceDailyNoteId: VisitInstanceDailyNoteId) =>
        api.post("./visit_daily_notes/:visitInstanceDailyNoteId/docare_upload", {
          path: { visitInstanceDailyNoteId },
        }),
    }),

  /**
   * Send a note's critical incident to Salesforce again.
   *
   * Only a failed incident, or one Salesforce sent back for review, can be sent again; the
   * server refuses any other with a conflict. Safe to press twice: Salesforce returns the
   * existing Case for the same incident rather than opening another.
   */
  resendCriticalIncident: () =>
    createMutation({
      mutationFn: (visitInstanceDailyNoteId: VisitInstanceDailyNoteId) =>
        api.post("./visit_daily_notes/:visitInstanceDailyNoteId/critical_incident/resend", {
          path: { visitInstanceDailyNoteId },
          body: { incidentSubtype: null },
        }),
    }),

  /**
   * Send failed DoCare uploads and Salesforce incidents again, several at once.
   *
   * The server answers per note, so one refused note (an incident that is not in a resendable
   * state any more, say) does not hide the others going through.
   */
  resubmitSyncIssues: () =>
    createMutation({
      mutationFn: (body: BodyOf<"post", "./visit_daily_notes/sync_issues/resubmit">) =>
        api.post("./visit_daily_notes/sync_issues/resubmit", { body }),
    }),

  /**
   * Sign one attestation over a batch of notes.
   *
   * The server refuses the whole batch if any note in it was replaced by a newer
   * submission in the meantime, so a partial success is not a state the caller has
   * to handle — it is either all signed or nothing was.
   */
  approve: () =>
    createMutation({
      mutationFn: (body: BodyOf<"post", "./visit_daily_notes/approve">) =>
        api.post("./visit_daily_notes/approve", { body }),
    }),

  /**
   * Store the signing nurse's own signature.
   *
   * Reached only after `approve` is refused with SIGNER_HAS_NO_SIGNATURE — the
   * signature is a precondition of signing that nothing checks up front, so this is
   * the recovery rather than part of the normal path.
   *
   * Writes the caller's own signature and nobody else's: the endpoint takes the
   * signer from the authenticated session, so there is no member id to pass.
   */
  saveSignerSignature: () =>
    createMutation({
      mutationFn: (body: BodyOf<"post", "./visit_daily_notes/signer_signature">) =>
        api.post("./visit_daily_notes/signer_signature", { body }),
    }),

  // Record a license a nurse holds; it decides which daily notes they may sign.
  createLicense: () =>
    createMutation({
      mutationFn: (body: BodyOf<"post", "./agency_member_licenses">) =>
        api.post("./agency_member_licenses", { body }),
    }),

  // Remove a license, so it no longer lets its holder sign. Answers with the updated list.
  removeLicense: () =>
    createMutation({
      mutationFn: (agencyMemberLicenseId: AgencyMemberLicenseId) =>
        api.post("./agency_member_licenses/:agencyMemberLicenseId/remove", {
          path: { agencyMemberLicenseId },
        }),
    }),

  /**
   * The case manager's second signature on a batch the nurse already signed.
   *
   * Refused if the batch never needed one, has already been co-signed, or was signed
   * by the same person — two signatures from one person are one signature.
   */
  cosign: () =>
    createMutation({
      mutationFn: (params: {
        attestationId: VisitInstanceDailyNoteAttestationId;
        body: BodyOf<
          "post",
          "./visit_daily_note_attestations/:visitInstanceDailyNoteAttestationId/cosign"
        >;
      }) =>
        api.post("./visit_daily_note_attestations/:visitInstanceDailyNoteAttestationId/cosign", {
          path: { visitInstanceDailyNoteAttestationId: params.attestationId },
          body: params.body,
        }),
    }),
}));
